import { createStore } from '@/utils/createStore'

export interface ConsumerRow {
  consumerId: string
  consumerName: string
  shareHolding: number        // Allocation %
  allocatedUnits?: number     // Allocated units (≤ plant's proposed annual generation in total)
  effectiveFrom?: string      // Allocation effective-from date
  effectiveTo?: string        // Allocation effective-to date (blank = open-ended)
  tariff: number              // Sakthi Power Tariff
  tnebTariff: number          // TNEB grid tariff
  tradeMargin: number
  status: 'active' | 'suspended'
}

/**
 * Approval state of a mapping — deliberately SEPARATE from `status`.
 *
 * `status` is the lifecycle flag ('active' | 'inactive') and is load-bearing:
 * ConsumerAllocationEditor's duplicate-group check only considers 'active'
 * groups, so folding approval values into that union would silently let
 * duplicate generator+plant groups through.
 */
export type MappingApprovalStatus = 'draft' | 'pending' | 'approved' | 'rejected'

export const APPROVAL_LABEL: Record<MappingApprovalStatus, string> = {
  draft: 'Draft',
  pending: 'Pending Approval',
  approved: 'Approved',
  rejected: 'Rejected',
}

export const APPROVAL_PILL: Record<MappingApprovalStatus, string> = {
  draft: 'pill-neutral',
  pending: 'pill-energy',
  approved: 'pill-green',
  rejected: 'pill-danger',
}

/** Rows saved before the approval gate existed have no value — treat as approved. */
export const approvalOf = (m: Pick<GeneratorMapping, 'approvalStatus'>): MappingApprovalStatus =>
  m.approvalStatus || 'draft'

export interface GeneratorMapping {
  id: string
  generatorId: string
  generatorName: string
  // ── Plant (a generator can have multiple plants — one group per gen+plant) ──
  plantId?: string
  plantName?: string
  htNumber?: string
  plantLocation?: string
  substation?: string
  plantCapacity?: number      // numeric Proposed Annual Generation (kWh/year) used to validate allocated units
  plantType: string
  capacity: string            // display Proposed Annual Generation, e.g. "5.0 MWh/yr"
  consumers: ConsumerRow[]
  totalAllocated: number      // total allocation %
  status: 'active' | 'inactive'
  lastUpdated: string
  // ── Approval (admin-only sign-off) ──
  approvalStatus?: MappingApprovalStatus
  submittedBy?: string | null
  submittedAt?: string | null
  // Null (not undefined) is used to CLEAR these on resubmit — JSON.stringify
  // drops undefined keys, so the API would never see the field and the old
  // approver/reason would survive on the row.
  approvedBy?: string | null
  approvedAt?: string | null
  approvalComments?: string | null
}

export const mappingStore = createStore<GeneratorMapping>('sp_mappings', [], 'M', 3, '/mappings')
